import { memo, useId } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'

import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import BodyText from 'LuxKit/Typography/BodyText'
import Clickable from 'components/Common/Clickable'
import Pane from 'components/Common/Pane/Pane'
import Group from 'components/utils/Group'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Expander from 'components/utils/Expander'
import useToggle from 'hooks/useToggle'

const Container = styled(Pane)`
  padding: ${rem(16)} ${rem(20)};
`

const RatingWrapper = styled.div`
  flex-shrink: 0;
  white-space: nowrap;
`

const MobileSummaryToggle = styled(Clickable)`
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
`

const MobileSummaryText = styled.div`
  flex: 1;
  min-width: 0;
`

interface Props {
  content: string
  score: number
  reviewsTotal: number
}

function OfferPageReviewAiSummary({
  content,
  score,
  reviewsTotal,
}: Props) {
  const intl = useIntl()
  const contentId = useId()
  const { value: mobileSummaryExpanded, toggle: toggleMobileSummaryExpanded } = useToggle(false)

  const summaryTitle = intl.formatMessage({
    id: 'uUdB4FuUUHKUZ8vXWn2C',
    defaultMessage: 'What guests say',
    description: 'Heading title for the AI-generated guest review summary section on the offer page',
  })
  const aiGeneratedLabel = intl.formatMessage({
    id: 'IZ2kMn_kaYATWxfAbhHu',
    defaultMessage: 'AI-generated based on customer reviews',
    description: 'Badge label indicating the review summary content is AI-generated on the offer page',
  })

  return (
    <Container background="neutral-seven">
      <CSSBreakpoint min="tablet">
        <Group direction="vertical" gap={12}>
          <Group direction="horizontal" gap={20} verticalAlign="start">
            <RatingWrapper>
              <NumberRating
                link
                rating={score}
                total={reviewsTotal}
                variant="medium"
                hideLabel
                layout="vertical"
                squareContainer
              />
            </RatingWrapper>
            <Group direction="vertical" gap={8}>
              <Group direction="vertical" tabletDirection="horizontal" gap={4} tabletGap={8} verticalAlign="center" tabletHorizontalAlign="space-between">
                <BodyText variant="large" weight="bold">{summaryTitle}</BodyText>
                <BodyText variant="small" colour="neutral-three">{aiGeneratedLabel}</BodyText>
              </Group>
              <BodyText variant="medium" colour="neutral-two">{content}</BodyText>
            </Group>
          </Group>
        </Group>
      </CSSBreakpoint>
      <CSSBreakpoint max="mobile">
        <Group direction="vertical" gap={12}>
          <Group direction="horizontal" gap={20} verticalAlign="start">
            <RatingWrapper>
              <NumberRating
                link
                rating={score}
                total={reviewsTotal}
                variant="medium"
                hideLabel
                layout="vertical"
                squareContainer
              />
            </RatingWrapper>
            <MobileSummaryToggle
              aria-controls={contentId}
              aria-expanded={mobileSummaryExpanded}
              onClick={toggleMobileSummaryExpanded}
            >
              <Group direction="horizontal" gap={12} horizontalAlign="space-between" verticalAlign="start">
                <MobileSummaryText>
                  <BodyText variant="large" weight="bold">{summaryTitle}</BodyText>
                  <BodyText variant="small" colour="neutral-three">{aiGeneratedLabel}</BodyText>
                </MobileSummaryText>
                <DropdownChevron open={mobileSummaryExpanded} />
              </Group>
            </MobileSummaryToggle>
          </Group>
          <Expander expanded={mobileSummaryExpanded}>
            <div
              aria-hidden={!mobileSummaryExpanded}
              data-testid="offer-page-review-ai-summary-content"
              id={contentId}
            >
              <BodyText variant="medium" colour="neutral-two">{content}</BodyText>
            </div>
          </Expander>
        </Group>
      </CSSBreakpoint>
    </Container>
  )
}

export default memo(OfferPageReviewAiSummary)
